"use client";
import React from 'react';
import { motion } from 'framer-motion';

const bodyPoints = [
    {
        num: '01',
        text: 'Posting consistently is important. But what matters more is how people perceive your brand after seeing your content.',
    },
    {
        num: '02',
        text: 'Every post, story, reel, comment, and campaign contributes to that perception. We help businesses create social media experiences that build credibility, strengthen customer relationships, and support every other part of the marketing funnel—from awareness and consideration to conversion.',
    },
];

export default function SocialFramework() {
    return (
        <section className="relative overflow-hidden py-28 px-6 md:px-16 lg:px-24" style={{ background: '#080400' }}>

            <div className="absolute pointer-events-none" style={{ width: 700, height: 700, top: '-20%', right: '-12%', background: 'radial-gradient(circle, rgba(206,255,26,0.04) 0%, transparent 60%)', borderRadius: '50%' }} />

            <div className="max-w-[1200px] mx-auto flex flex-col gap-16 relative z-10">

                <motion.span
                    initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#ceff1a', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                >
                    Why Businesses Choose Lead Workz
                </motion.span>

                {/* ACTIVITY vs INFLUENCE */}
                <motion.div
                    initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="grid grid-cols-1 md:grid-cols-2 overflow-hidden"
                    style={{ border: '1px solid rgba(206,255,26,0.12)', borderRadius: 24 }}
                >
                    {/* Left: ACTIVITY */}
                    <div className="px-6 py-9 md:px-11 md:py-12" style={{
                        borderRight: '1px solid rgba(206,255,26,0.10)',
                        display: 'flex', flexDirection: 'column', gap: 24, background: 'rgba(255,255,255,0.015)',
                    }}>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', color: 'rgba(251,253,253,0.22)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                            Most Social Media Agencies
                        </span>
                        <p style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', fontSize: 'clamp(14px, 1.2vw, 17px)', lineHeight: 1.7, color: 'rgba(251,253,253,0.35)', letterSpacing: '0.025em' }}>
                            Most social media agencies focus on
                        </p>
                        <div className="flex flex-wrap items-center gap-4">
                            <span style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 800, fontSize: 'clamp(30px, 9vw, 88px)', letterSpacing: '-0.03em', lineHeight: 1, color: 'rgba(251,253,253,0.10)', textDecoration: 'line-through', textDecorationColor: 'rgba(251,253,253,0.18)', textDecorationThickness: '3px' }}>
                                ACTIVITY
                            </span>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', border: '1.5px solid rgba(251,253,253,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 2l8 8M10 2l-8 8" stroke="rgba(251,253,253,0.22)" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Right: INFLUENCE */}
                    <div className="px-6 py-9 md:px-11 md:py-12" style={{
                        display: 'flex', flexDirection: 'column', gap: 24, background: 'rgba(206,255,26,0.025)',
                    }}>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', color: 'rgba(206,255,26,0.60)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                            Lead Workz
                        </span>
                        <p style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', fontSize: 'clamp(14px, 1.2vw, 17px)', lineHeight: 1.7, color: 'rgba(251,253,253,0.75)', letterSpacing: '0.025em' }}>
                            We focus on
                        </p>
                        <div className="flex flex-wrap items-center gap-4">
                            <span style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 800, fontSize: 'clamp(30px, 9vw, 88px)', letterSpacing: '-0.03em', lineHeight: 1, color: '#ceff1a' }}>
                                INFLUENCE
                            </span>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', border: '1.5px solid rgba(206,255,26,0.35)', background: 'rgba(206,255,26,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 6l3 3 5-5" stroke="#ceff1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Body points */}
                <div className="flex flex-col gap-0">
                    {bodyPoints.map((pt, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: i * 0.14 }}
                            className="flex items-start gap-8 py-8"
                            style={{ borderBottom: i < bodyPoints.length - 1 ? '1px solid rgba(206,255,26,0.08)' : 'none' }}
                        >
                            <span style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 13, color: 'rgba(206,255,26,0.40)', letterSpacing: '0.15em', flexShrink: 0, marginTop: 3, minWidth: 28 }}>
                                {pt.num}
                            </span>
                            <motion.div
                                initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.1 + i * 0.14 }}
                                style={{ width: 2, minHeight: 48, background: 'linear-gradient(180deg, #ceff1a 0%, rgba(206,255,26,0.10) 100%)', borderRadius: 2, flexShrink: 0, alignSelf: 'stretch', transformOrigin: 'top' }}
                            />
                            <p style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', fontSize: 'clamp(15px, 1.4vw, 19px)', lineHeight: 1.75, color: 'rgba(251,253,253,0.68)', letterSpacing: '0.025em' }}>
                                {pt.text}
                            </p>
                        </motion.div>
                    ))}
                </div>

                {/* Manifesto quote */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    style={{ borderTop: '1px solid rgba(206,255,26,0.12)', paddingTop: 48, display: 'flex', flexDirection: 'column', gap: 20 }}
                >
                    <span style={{ fontFamily: 'Georgia, serif', fontSize: 80, lineHeight: 0.7, color: 'rgba(206,255,26,0.18)', display: 'block' }}>&ldquo;</span>
                    <p style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 'clamp(26px, 3.5vw, 52px)', lineHeight: 1.15, letterSpacing: '-0.01em', color: '#fbfdfd', maxWidth: 820 }}>
                        Social media isn&apos;t just where brands are{' '}
                        <span style={{ color: 'rgba(251,253,253,0.30)', textDecoration: 'line-through', textDecorationColor: '#ceff1a', fontStyle: 'italic' }}>seen</span>.
                        {' '}It&apos;s where brands are{' '}
                        <span style={{ color: '#ceff1a' }}>judged</span>.
                        {' '}And ultimately, where{' '}
                        <span style={{ color: '#ceff1a' }}>trust begins</span>.
                    </p>
                </motion.div>

            </div>
        </section>
    );
}
